import { Form, FormValues, Input } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Modal from '../../../../shared/components/modal/modal';
import { handleServerFormErrors } from '../../../../shared/utils/error-handling/error-handling';
import { useAppSelector } from '../../../../store/hooks';
import { requestPreviewAlertId } from '../../../request/constants/constants';
import { setActiveRequestId } from '../../../request/store/requests-slice';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useAttachSignRequestMutation } from '../../api/sign-requests-api';
import { signRequestAlertId, signRequestMoveModalId } from '../../constants/constants';
import {
  selectActiveSignRequestId,
  selectTempSignRequestId,
  setTempSignRequestId,
} from '../../store/sign-requests-slice';

const SignRequestMoveFormModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const [form] = Form.useForm<{ shortId: string }>();
  const dispatch = useDispatch();
  const { t } = useTranslation(['common', 'sign-requests']);
  const tempSignRequestId = useAppSelector(selectTempSignRequestId);
  const activeSignRequestId = useAppSelector(selectActiveSignRequestId);
  const [attachRequest, { isLoading }] = useAttachSignRequestMutation();

  const closeFormModal = () => {
    dispatch(closeModal(signRequestMoveModalId));
    dispatch(setTempSignRequestId(null));
  };

  const setSignRequestMoveAlertMessage = (alertId: string) => {
    dispatch(
      setAlert({
        id: alertId,
        type: 'success',
        title: t('signScanRequest.message.requestMoved'),
      }),
    );
  };

  const submitForm = (formValues: FormValues<typeof form>) => {
    if (!tempSignRequestId?.length) {
      return;
    }
    attachRequest({
      signRequestId: tempSignRequestId,
      ...formValues,
    })
      .unwrap()
      .then(response => {
        closeFormModal();

        if (tempSignRequestId === activeSignRequestId) {
          dispatch(setActiveRequestId(response.request));
        }

        setSignRequestMoveAlertMessage(signRequestAlertId);
        setSignRequestMoveAlertMessage(requestPreviewAlertId);
      })
      .catch(error => {
        handleServerFormErrors(
          form,
          error.data?.errors || { shortId: [t('signScanRequest.validationError.errorOccurred')] },
        );
      });
  };

  useEffect(() => {
    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Modal
      title={t('moveRequestFormModal.title', { ns: 'sign-requests' })}
      onCancel={closeFormModal}
      isLoading={isLoading}
      customSubmitText={t('action.submit')}
      onOk={() => form.submit()}
      formId={signRequestMoveModalId}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      <Form id={signRequestMoveModalId} form={form} layout="vertical" onFinish={submitForm}>
        <Form.Item
          name="shortId"
          label={t('signScanRequest.label.shortId')}
          rules={[{ required: true, message: t('signScanRequest.validationError.requestRequired') }]}
        >
          <Input placeholder={t('signScanRequest.label.shortId')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const SignRequestMoveFormModal = (): JSX.Element => {
  return <LazyModal modalId={signRequestMoveModalId} Component={SignRequestMoveFormModalContent} />;
};

export default SignRequestMoveFormModal;
